import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

const MissionLog: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/');
  };

  const blogPosts = [
    {
      id: "LOG_001",
      timestamp: "2024.03.15_14:32:08",
      title: "Building the Neural Interface: A Journey into Cyberpunk Web Design",
      excerpt: "Documenting the creation of a futuristic personal website that bridges medical precision with technological innovation...",
      status: "PUBLISHED",
      readTime: "8 min",
      tags: ["webdev", "design", "cyberpunk"]
    },
    {
      id: "LOG_002",
      timestamp: "2024.03.10_09:15:42",
      title: "Medical AI Integration in Surgical Planning",
      excerpt: "Exploring how artificial intelligence is revolutionizing surgical procedures and patient outcomes in modern medicine...",
      status: "DRAFT",
      readTime: "12 min",
      tags: ["medicine", "ai", "surgery"]
    },
    {
      id: "LOG_003",
      timestamp: "2024.03.05_16:20:11",
      title: "The Art of FPV Cinematography: Technical Precision Meets Creative Vision",
      excerpt: "Breaking down the technical aspects of drone cinematography and how surgical precision translates to aerial filming...",
      status: "PUBLISHED",
      readTime: "6 min",
      tags: ["fpv", "cinematography", "drones"]
    },
    {
      id: "LOG_004",
      timestamp: "2024.02.28_11:45:33",
      title: "Fullstack Development for Medical Applications",
      excerpt: "Building secure, scalable web applications for healthcare environments using modern technologies...",
      status: "PUBLISHED",
      readTime: "15 min",
      tags: ["fullstack", "medical", "security"]
    },
    {
      id: "LOG_005",
      timestamp: "2024.02.20_20:18:07",
      title: "Night Photography: Capturing Light in Darkness",
      excerpt: "Technical insights into low-light photography techniques and the equipment that makes it possible...",
      status: "DRAFT",
      readTime: "10 min",
      tags: ["photography", "nightshot", "technical"]
    },
    {
      id: "LOG_006",
      timestamp: "2024.02.15_13:22:56",
      title: "The Duality of a Medical Tech Creator",
      excerpt: "Reflections on balancing a career in medicine with passion projects in technology and creative arts...",
      status: "PUBLISHED",
      readTime: "7 min",
      tags: ["personal", "career", "balance"]
    }
  ];

  const getStatusColor = (status: string) => {
    return status === 'PUBLISHED' ? 'text-green-400' : 'text-yellow-400';
  };

  return (
    <div className="min-h-screen bg-black text-white font-rajdhani relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 z-[1] opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 to-black"></div>
      </div>

      {/* Terminal scanlines */}
      <div className="fixed inset-0 z-[2] pointer-events-none">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(34, 197, 94, 0.05) 2px, rgba(34, 197, 94, 0.05) 4px)',
            backgroundSize: '100% 4px'
          }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10 pt-24 pb-16 px-4">
        <BackButton onClick={handleBack} />
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="text-green-400 font-mono text-lg mb-4 cyber-glow">
            bash@neural-core:~$ tail -f /var/log/personal_log.txt
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            MISSION_LOG
          </h1>
          <p className="text-gray-400 text-xl max-w-4xl mx-auto">
            Digital Chronicles • Technical Documentation • Personal Reflections
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          {/* Terminal Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-black/80 border border-green-400/30 rounded-t-lg p-4 mb-0"
          >
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <div className="ml-4 text-green-400 font-mono text-sm">
                bash@neural-core:~/blog$ ls -la --sort=time
              </div>
            </div>
          </motion.div>

          {/* Blog Posts Terminal List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-black/90 border-l border-r border-b border-green-400/30 rounded-b-lg p-6"
          >
            <div className="space-y-1 font-mono text-sm">
              {/* Terminal ls header */}
              <div className="text-gray-500 mb-4">
                total {blogPosts.length} entries
              </div>
              
              {blogPosts.map((post, i) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + (i * 0.1) }}
                  className="group hover:bg-green-400/10 p-3 rounded cursor-pointer transition-all duration-300"
                >
                  {/* Terminal ls format */}
                  <div className="flex items-center space-x-4 mb-2">
                    <span className="text-gray-500">
                      {post.status === 'PUBLISHED' ? '-rw-r--r--' : '-rw-------'}
                    </span>
                    <span className="text-white">1</span>
                    <span className="text-gray-400">bash</span>
                    <span className="text-gray-400">users</span>
                    <span className="text-gray-500 w-16">{Math.floor(Math.random() * 50) + 10}K</span>
                    <span className="text-gray-500">{post.timestamp}</span>
                    <span className={`${getStatusColor(post.status)} font-bold`}>
                      {post.id}.md
                    </span>
                  </div>
                  
                  {/* Blog post content */}
                  <div className="ml-8 border-l-2 border-green-400/30 pl-4 group-hover:border-green-400 transition-colors">
                    <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-green-400 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-400 mb-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {post.tags.map((tag, idx) => (
                            <span key={idx} className="text-xs bg-green-400/20 text-green-400 px-2 py-1 rounded">
                              #{tag}
                            </span>
                          ))}
                        </div>
                        <span className="text-gray-500 text-xs">{post.readTime} read</span>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <span className={`text-xs ${getStatusColor(post.status)}`}>
                          {post.status}
                        </span>
                        <motion.div
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          whileHover={{ scale: 1.1 }}
                        >
                          <span className="text-green-400 text-xs cursor-pointer">
                            [READ] &gt;&gt;
                          </span>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {/* Terminal prompt */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="mt-6 pt-4 border-t border-green-400/30"
              >
                <div className="text-green-400">
                  bash@neural-core:~/blog$ <span className="animate-pulse">█</span>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Blog Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <div className="bg-black/60 border border-green-400/30 p-6 rounded-lg text-center">
              <div className="text-3xl text-green-400 font-bold mb-2">
                {blogPosts.filter(post => post.status === 'PUBLISHED').length}
              </div>
              <div className="text-gray-400">Published Posts</div>
            </div>
            
            <div className="bg-black/60 border border-yellow-400/30 p-6 rounded-lg text-center">
              <div className="text-3xl text-yellow-400 font-bold mb-2">
                {blogPosts.filter(post => post.status === 'DRAFT').length}
              </div>
              <div className="text-gray-400">Drafts in Progress</div>
            </div>
            
            <div className="bg-black/60 border border-blue-400/30 p-6 rounded-lg text-center">
              <div className="text-3xl text-blue-400 font-bold mb-2">∞</div>
              <div className="text-gray-400">Ideas Brewing</div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default MissionLog;